import { ApolloClient, gql, useMutation, useQuery } from "@apollo/client";
import { useRouter } from "next/router";
import { ParsedUrlQuery } from "node:querystring";
import Error from "../components/Error/Error";
import Loading from "../components/Loading/Loading";
import ProcedureResultsGrid from "../components/ProcedureResultsGrid/ProcedureResultsGrid";
import ProcedureSectionsGrid from "../components/ProcedureSectionsGrid/ProcedureSectionsGrid";
import { appName, getPublicRuntimeConfig } from "../config";
import { isTruthy } from "../core/classSet";
import { ensureError } from "../core/error";
import { getIndustrySlug, getSearchQuery, getTopicSlug } from "../core/getParam";
import { useDidUpdate } from "../core/hooks/useDidUpdate";
import logger from "../core/logger";
import { makeMoreFactory } from "../core/paginationFactory";
import {
  ILogUserSearchMutationMutation,
  ILogUserSearchMutationMutationVariables,
  IProcedureListingMoreQuery,
  IProcedureListingMoreQueryVariables,
  IProcedureListingQuery,
  IProcedureListingQueryVariables,
} from "../graphql/gpl-graphQLTypes.gen";
import PageLayoutContainer from "./Layouts/PageLayoutContainer";
import ProcedureListingFragment from "./ProcedureListingFragment";

const procedureListingQuery = gql`
  query ProcedureListing(
    $industrySlug: String
    $topicSlug: String
    $searchQuery: String
    $useProcedureList: Boolean!
  ) {
    industry(slug: $industrySlug) {
      slug
      label
    }
    topic(slug: $topicSlug) {
      slug
      label
    }
    proceduresSummary(filters: { industry: $industrySlug, topic: $topicSlug }, limit: 50)
      @skip(if: $useProcedureList) {
      slug
      label
      totalCount
      type
      data {
        ...ProcedureListingFragment
      }
    }
    procedures(
      filters: { industry: $industrySlug, topic: $topicSlug, searchQuery: $searchQuery }
      pagination: { limit: 10 }
    ) @include(if: $useProcedureList) {
      data {
        ...ProcedureListingFragment
      }
      hasMore
      cursor
      count
    }
  }
  ${ProcedureListingFragment}
`;

const procedureListingMoreQuery = gql`
  query ProcedureListingMore(
    $industrySlug: String
    $topicSlug: String
    $searchQuery: String
    $pagination: PaginationInput!
  ) {
    procedures(
      filters: { industry: $industrySlug, topic: $topicSlug, searchQuery: $searchQuery }
      pagination: $pagination
    ) {
      data {
        ...ProcedureListingFragment
      }
      hasMore
      cursor
    }
  }
  ${ProcedureListingFragment}
`;

const logUserSearchMutation = gql`
  mutation LogUserSearchMutation($input: LogUserSearchInput!) {
    logUserSearch(input: $input) {
      success
      errors {
        fieldPath
        fieldValue
        error
      }
    }
  }
`;

const useFetchMoreProcedures = makeMoreFactory<
  IProcedureListingMoreQuery,
  IProcedureListingMoreQueryVariables
>(
  procedureListingMoreQuery,
  40,
)("procedures");

function inspectQueryUrl(arg: { query: ParsedUrlQuery }) {
  let industrySlug: string | undefined = getIndustrySlug(arg);
  if (industrySlug === "all") {
    industrySlug = undefined;
  }
  let topicSlug: string | undefined = getTopicSlug(arg);
  if (topicSlug === "all") {
    topicSlug = undefined;
  }
  const searchQuery = getSearchQuery(arg);
  // Use list if both industry and topic are set or if the user is searching
  const useProcedureList = !!(industrySlug && topicSlug) || !!searchQuery;
  const variables: IProcedureListingQueryVariables = {
    industrySlug,
    topicSlug,
    searchQuery,
    useProcedureList,
  };

  return variables;
}

ProcedureListingContainer.runDataQueries = (
  context: { query: ParsedUrlQuery },
  apolloClient: ApolloClient<any>,
) => {
  const variables = inspectQueryUrl(context);

  return Promise.all([
    PageLayoutContainer.runDataQueries(apolloClient),
    apolloClient.query<IProcedureListingQuery, IProcedureListingQueryVariables>({
      query: procedureListingQuery,
      variables: variables,
    }),
  ]);
};

export default function ProcedureListingContainer() {
  const router = useRouter();

  const variables = inspectQueryUrl(router);
  const { data, loading, error, onFetchMoreProcedures } = useFetchMoreProcedures(
    useQuery<IProcedureListingQuery, IProcedureListingQueryVariables>(procedureListingQuery, {
      variables,
    }),
    variables,
    "onFetchMoreProcedures",
  );

  const { industrySlug, topicSlug, searchQuery } = variables;

  const [logUserSearch] = useMutation<
    ILogUserSearchMutationMutation,
    ILogUserSearchMutationMutationVariables
  >(logUserSearchMutation);

  useDidUpdate(() => {
    if (searchQuery) {
      logUserSearch({
        variables: { input: { industrySlug, topicSlug, search: searchQuery } },
      }).catch((err) => {
        logger.error(ensureError(err), { from: "logUserSearch" });
      });
    }
  }, [searchQuery]);

  function getSubTitle() {
    if (!industrySlug) {
      return "All Industries";
    }
    if (data?.industry?.label) {
      return data.industry.label;
    }
    return industrySlug;
  }

  function renderContent() {
    if (data) {
      if (variables.useProcedureList) {
        return (
          <ProcedureResultsGrid
            data={data?.procedures?.data}
            title={searchQuery ?? data.topic?.label ?? topicSlug ?? "All Procedures"}
            subTitle={getSubTitle()}
            totalCount={data.procedures?.count || 0}
            onFetchMore={onFetchMoreProcedures}
          />
        );
      } else {
        return (
          <ProcedureSectionsGrid
            sections={data.proceduresSummary ?? []}
            title={data?.industry?.label ?? data?.topic?.label ?? "All Procedures"}
          />
        );
      }
    }

    if (loading) {
      return <Loading />;
    }

    if (error) {
      return <Error error={error} />;
    }

    return null;
  }

  const getTitle = () => {
    const labels = [
      data?.industry?.label ?? industrySlug,
      data?.topic?.label ?? topicSlug,
      searchQuery,
    ].filter(isTruthy);
    if (labels.length) {
      return `${labels.join(" - ")} | ${appName}`;
    }
    return `${appName} | MaintainX`;
  };

  function getDescription() {
    if (data?.industry?.label && data?.topic?.label) {
      return `${data.industry.label} - ${data.topic.label} procedures to improve your maintenance, safety, and operations.`;
    }
    if (data?.industry?.label) {
      return `${data.industry.label} procedures to improve your maintenance, safety, and operations.`;
    }
    return undefined;
  }

  function getCanonicalUrl() {
    if (industrySlug && topicSlug) {
      return `${getPublicRuntimeConfig().frontendUrl}/${industrySlug}/${topicSlug}`;
    }
    if (industrySlug) {
      return `${getPublicRuntimeConfig().frontendUrl}/${industrySlug}`;
    }
    return undefined;
  }

  return (
    <PageLayoutContainer
      metaDescription={getDescription()}
      metaTitle={getTitle()}
      metaUrl={getCanonicalUrl()}
    >
      {renderContent()}
    </PageLayoutContainer>
  );
}
